import { type ScopeType } from '@apps/web/features/admin_agent_scratchpad/model/constants';
import { useApiQuery } from '@apps/web/swr_data_fetching/use_api_query';
import {
    type AdminScratchpadIndexResponse,
    type AdminScratchpadNoteReadResponse,
    type AdminScratchpadRevisionListResponse,
    type AdminScratchpadRunNotesResponse,
    type AdminScratchpadScopeRegistryResponse,
    type ApiQueryParamsType,
} from '@libraries/typescript/api_client';

type IndexQueryParams = ApiQueryParamsType<'/admin/agent-scratchpad', 'get'>;

export type AdminScratchpadIndexParams = {
    readonly page?: number;
    readonly perPage?: number;
    readonly scopeType?: ReadonlyArray<ScopeType>;
    readonly scopeId?: string;
    readonly tag?: ReadonlyArray<string>;
    readonly volatility?: NonNullable<IndexQueryParams>['volatility'];
    readonly freshness?: NonNullable<IndexQueryParams>['freshness'];
    readonly search?: string;
    readonly includeRetired?: boolean;
    // Default false: the API rejects unscoped listings so we never
    // silently span every organization. Set true only when the caller
    // has explicitly opted in via the UI's cross-scope toggle.
    readonly crossScope?: boolean;
    readonly updatedAfter?: string;
    readonly updatedBefore?: string;
    readonly sort?: NonNullable<IndexQueryParams>['sort'];
    readonly direction?: NonNullable<IndexQueryParams>['direction'];
};

/**
 * Vault-style listing. Empty query returns most-recently-updated notes; when
 * `search` is set, the API vector-ranks via Turbopuffer and returns
 * ``degraded_search=true`` on fallback so the UI can flag it.
 */
export function useAdminAgentScratchpadIndex(
    params: AdminScratchpadIndexParams = {},
    options: {
        readonly keepPreviousData?: boolean;
        readonly enabled?: boolean;
    } = {},
) {
    const queryParams = {
        ...(params.page !== undefined ? { page: params.page } : {}),
        ...(params.perPage !== undefined ? { per_page: params.perPage } : {}),
        ...(params.scopeType && params.scopeType.length > 0
            ? { scope_type: [...params.scopeType] }
            : {}),
        ...(params.scopeId !== undefined && params.scopeId !== ''
            ? { scope_id: params.scopeId }
            : {}),
        ...(params.tag && params.tag.length > 0
            ? { tag: [...params.tag] }
            : {}),
        ...(params.volatility !== undefined
            ? { volatility: params.volatility }
            : {}),
        ...(params.freshness !== undefined
            ? { freshness: params.freshness }
            : {}),
        ...(params.search !== undefined && params.search.trim() !== ''
            ? { search: params.search.trim() }
            : {}),
        ...(params.includeRetired !== undefined
            ? { include_retired: params.includeRetired }
            : {}),
        ...(params.crossScope !== undefined
            ? { cross_scope: params.crossScope }
            : {}),
        ...(params.updatedAfter !== undefined
            ? { updated_after: params.updatedAfter }
            : {}),
        ...(params.updatedBefore !== undefined
            ? { updated_before: params.updatedBefore }
            : {}),
        ...(params.sort !== undefined ? { sort: params.sort } : {}),
        ...(params.direction !== undefined
            ? { direction: params.direction }
            : {}),
    };

    return useApiQuery<
        '/admin/agent-scratchpad',
        'get',
        AdminScratchpadIndexResponse,
        AdminScratchpadIndexResponse
    >({
        path: '/admin/agent-scratchpad',
        method: 'get',
        queryParams:
            Object.keys(queryParams).length > 0
                ? (queryParams as IndexQueryParams)
                : undefined,
        enabled: options.enabled ?? true,
        select: (response) => response,
        keepPreviousData: options.keepPreviousData ?? true,
    });
}

export function useAdminAgentScratchpadNote(noteId: string | null) {
    return useApiQuery<
        '/admin/agent-scratchpad/{note_id}',
        'get',
        AdminScratchpadNoteReadResponse,
        AdminScratchpadNoteReadResponse
    >({
        path: '/admin/agent-scratchpad/{note_id}',
        method: 'get',
        pathParams: noteId !== null ? { note_id: noteId } : undefined,
        enabled: noteId !== null,
        select: (response) => response,
    });
}

export function useAdminAgentScratchpadNoteRevisions(
    noteId: string | null,
    params: { readonly page?: number; readonly perPage?: number } = {},
    options: { readonly enabled?: boolean } = {},
) {
    type RevisionsParams = ApiQueryParamsType<
        '/admin/agent-scratchpad/{note_id}/revisions',
        'get'
    >;
    const queryParams: RevisionsParams = {
        ...(params.page !== undefined ? { page: params.page } : {}),
        ...(params.perPage !== undefined ? { per_page: params.perPage } : {}),
    };
    const enabled = (options.enabled ?? true) && noteId !== null;
    return useApiQuery<
        '/admin/agent-scratchpad/{note_id}/revisions',
        'get',
        AdminScratchpadRevisionListResponse,
        AdminScratchpadRevisionListResponse
    >({
        path: '/admin/agent-scratchpad/{note_id}/revisions',
        method: 'get',
        pathParams: noteId !== null ? { note_id: noteId } : undefined,
        queryParams:
            Object.keys(queryParams).length > 0 ? queryParams : undefined,
        enabled,
        select: (response) => response,
    });
}

/**
 * Registry names the tool/skill scope-id pickers autocomplete against.
 *
 * Loaded lazily by the filter bar so the initial index render doesn't wait
 * on the registry roundtrip. Kept cached across the session — the registry
 * only changes with a deploy, so revalidateOnFocus/interval churn would
 * just burn requests.
 */
export function useAdminAgentScratchpadScopeRegistry(
    options: { readonly enabled?: boolean } = {},
) {
    return useApiQuery<
        '/admin/agent-scratchpad/scope-registry',
        'get',
        AdminScratchpadScopeRegistryResponse,
        AdminScratchpadScopeRegistryResponse
    >({
        path: '/admin/agent-scratchpad/scope-registry',
        method: 'get',
        enabled: options.enabled ?? true,
        revalidateOnFocus: false,
        select: (response) => response,
    });
}

export function useAdminAgentScratchpadNotesForRun(
    runId: string | null,
    options: { readonly enabled?: boolean } = {},
) {
    const enabled = (options.enabled ?? true) && runId !== null;
    return useApiQuery<
        '/admin/agent-scratchpad/by-run/{run_id}',
        'get',
        AdminScratchpadRunNotesResponse,
        AdminScratchpadRunNotesResponse
    >({
        path: '/admin/agent-scratchpad/by-run/{run_id}',
        method: 'get',
        pathParams: runId !== null ? { run_id: runId } : undefined,
        enabled,
        select: (response) => response,
    });
}
